import { zodResolver } from '@hookform/resolvers/zod';
import { DateField } from '@savvy-kit/patterns/date-field';
import InputField from '@savvy-kit/patterns/input-field';
import { DateTime } from 'luxon';
import { Controller, useForm } from 'react-hook-form';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { LIABILITY_TYPE_MORTGAGE } from '@savvy/constants/liability-types';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { LiabilityFormValues } from './add-account-schemas';
import {
  ADD_ACCOUNT_SAVE_ERROR,
  LIABILITY_FORM_TITLE,
  LIABILITY_TYPE_OPTIONS,
  MORTGAGE_FORM_TITLE,
  resolveLiabilityType,
} from './add-account-constants';
import { AddAccountFormShell } from './add-account-form-shell';
import { CLIENT_PORTAL_CREATE_LIABILITY } from './add-account-graphql';
import { AddAccountNumericField } from './add-account-numeric-field';
import { liabilityFormSchema } from './add-account-schemas';
import { AddAccountSelectField } from './add-account-select-field';

type AddAccountLiabilityFormProps = {
  readonly onCancel: () => void;
  readonly onCreated: () => Promise<unknown>;
  readonly cancelLabel?: string;
  readonly onStepBack?: () => void;
  readonly mortgageForAssetId?: string;
  readonly submitLabel: string;
};

export function AddAccountLiabilityForm({
  onCancel,
  cancelLabel,
  onStepBack,
  onCreated,
  mortgageForAssetId,
  submitLabel,
}: AddAccountLiabilityFormProps) {
  const [createLiability, { loading }] = useMutationWithErrorLogging(CLIENT_PORTAL_CREATE_LIABILITY);
  const isMortgage = mortgageForAssetId !== undefined;
  const { control, handleSubmit, formState } = useForm<LiabilityFormValues>({
    resolver: zodResolver(liabilityFormSchema),
    defaultValues: {
      name: isMortgage ? 'Mortgage' : '',
      providerName: '',
      type: isMortgage ? LIABILITY_TYPE_MORTGAGE : '',
      currentBalance: '',
      originalBalance: '',
      annualInterestPercent: '',
    },
  });

  const submit = handleSubmit(async (values) => {
    const type = resolveLiabilityType(values.type);
    if (type === undefined) {
      return;
    }
    try {
      await createLiability({
        variables: {
          input: {
            acquiredAt: DateTime.fromJSDate(values.acquiredAt).toISODate(),
            annualInterestPercent: values.annualInterestPercent === '' ? undefined : values.annualInterestPercent,
            assetId: mortgageForAssetId === undefined ? undefined : Number(mortgageForAssetId),
            currencyCode: 'USD',
            currentBalance: formatCurrencyForSubmit(values.currentBalance) ?? '',
            name: values.name,
            originalBalance: formatCurrencyForSubmit(values.originalBalance) ?? '',
            providerName: values.providerName === '' ? undefined : values.providerName,
            type,
          },
        },
      });
      await onCreated();
    } catch (error) {
      handleMutationError(error, ADD_ACCOUNT_SAVE_ERROR);
    }
  });

  return (
    <AddAccountFormShell
      title={isMortgage ? MORTGAGE_FORM_TITLE : LIABILITY_FORM_TITLE}
      onCancel={onCancel}
      cancelLabel={cancelLabel}
      onStepBack={onStepBack}
      onSubmit={submit}
      submitLabel={submitLabel}
      submitting={loading}
    >
      {!isMortgage && (
        <>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <InputField
                {...field}
                id="add-liability-name"
                label="Name"
                required
                autoFocus
                error={formState.errors.name !== undefined}
                errorMessage={formState.errors.name?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <AddAccountSelectField
                id="add-liability-type"
                label="Type"
                value={field.value}
                options={LIABILITY_TYPE_OPTIONS}
                onValueChange={field.onChange}
                errorMessage={formState.errors.type?.message}
              />
            )}
          />
        </>
      )}
      <Controller
        control={control}
        name="providerName"
        render={({ field }) => (
          <InputField
            {...field}
            id="add-liability-provider"
            label="Lender"
            description="Optional"
            error={formState.errors.providerName !== undefined}
            errorMessage={formState.errors.providerName?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="currentBalance"
        render={({ field }) => (
          <AddAccountNumericField
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            maxDecimals={2}
            id="add-liability-current-balance"
            label="Current balance"
            required
            prependInputAddon="$"
            errorMessage={formState.errors.currentBalance?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="originalBalance"
        render={({ field }) => (
          <AddAccountNumericField
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            maxDecimals={2}
            id="add-liability-original-balance"
            label={isMortgage ? 'Original mortgage amount' : 'Original amount'}
            required
            prependInputAddon="$"
            errorMessage={formState.errors.originalBalance?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="acquiredAt"
        render={({ field }) => (
          <DateField
            id="add-liability-acquired-at"
            label="Start date"
            required
            value={field.value}
            onChange={field.onChange}
            maxDate={new Date()}
            error={formState.errors.acquiredAt !== undefined}
            errorMessage={formState.errors.acquiredAt?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="annualInterestPercent"
        render={({ field }) => (
          <AddAccountNumericField
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            maxDecimals={4}
            id="add-liability-interest"
            label="Interest rate"
            description="Optional"
            appendInputAddon="%"
            errorMessage={formState.errors.annualInterestPercent?.message}
          />
        )}
      />
    </AddAccountFormShell>
  );
}
